import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Camera,
  User,
  Check,
  MapPin,
  Loader2,
  LayoutDashboard,
  Users,
  MessageSquare,
  Calendar,
  Sparkles,
  ArrowRight,
} from "lucide-react";

import { useAuth } from "../../contexts/AuthProvider";
import { userApi, UpdateProfileData } from "../../services/userApi";
import { authApi } from "../../services/api";
import { LocationAutocomplete, LocationData } from "../LocationAutocomplete";
import { Button } from "../common/Button";
import { LoadingSpinner } from "../LoadingSpinner";
import { DIETARY_OPTIONS } from "../../constants/filterOptions";

export type OnboardingMode = "full" | "profile" | "tour";

interface UnifiedOnboardingWizardProps {
  mode: OnboardingMode;
  onComplete: () => void;
  onSkip: () => void;
}

interface ProfileFormData {
  firstName: string;
  lastName: string;
  location: LocationData | null;
  address: string;
  preferences: string[];
  favoriteCuisines: string[];
  dietaryRestrictions: string[];
  accessibilityNeeds: string[];
  zipCode: string;
  timezone: string;
  avatarUrl: string | null;
}

// Step types
type StepType =
  | "welcome"
  | "name"
  | "location"
  | "cuisines"
  | "dietary"
  | "dashboard-tour"
  | "meetup-tour"
  | "support-tour"
  | "complete";

interface Step {
  id: StepType;
  category: "profile" | "tour";
  required?: boolean; // If true, step is always shown
  condition?: (formData: ProfileFormData, user: any) => boolean;
}

// Define all possible steps
const ALL_STEPS: Step[] = [
  { id: "welcome", category: "profile", required: true }, // Welcome + Name combined
  { id: "location", category: "profile", required: true },
  { id: "cuisines", category: "profile", required: true }, // Always show cuisine selection
  { id: "dietary", category: "profile" }, // Optional, shown if previous profile steps shown
  { id: "dashboard-tour", category: "tour", required: true },
  { id: "meetup-tour", category: "tour", required: true },
  { id: "support-tour", category: "tour", required: true },
  { id: "complete", category: "tour", required: true },
];

const CUISINE_OPTIONS = [
  { value: "italian", label: "Italian", icon: "🍝" },
  { value: "mexican", label: "Mexican", icon: "🌮" },
  { value: "chinese", label: "Chinese", icon: "🥡" },
  { value: "japanese", label: "Japanese", icon: "🍣" },
  { value: "american", label: "American", icon: "🍔" },
  { value: "thai", label: "Thai", icon: "🍜" },
  { value: "indian", label: "Indian", icon: "🍛" },
  { value: "mediterranean", label: "Mediterranean", icon: "🫒" },
  { value: "french", label: "French", icon: "🥐" },
  { value: "korean", label: "Korean", icon: "🥘" },
  { value: "vietnamese", label: "Vietnamese", icon: "🍲" },
  { value: "greek", label: "Greek", icon: "🥙" },
];

const WHEELCHAIR_ACCESSIBILITY_OPTIONS = [
  {
    id: "wheelchair_accessible_entrance",
    label: "Wheelchair Accessible Entrance",
    icon: "♿",
  },
  {
    id: "wheelchair_accessible_parking",
    label: "Wheelchair Accessible Parking",
    icon: "🅿️",
  },
  {
    id: "wheelchair_accessible_restroom",
    label: "Wheelchair Accessible Restroom",
    icon: "🚻",
  },
  {
    id: "wheelchair_accessible_seating",
    label: "Wheelchair Accessible Seating",
    icon: "🪑",
  },
];

const generateTimezoneOptions = () => {
  const commonTimezones = [
    "UTC",
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "America/Phoenix",
    "America/Anchorage",
    "Pacific/Honolulu",
    "Europe/London",
    "Europe/Paris",
    "Europe/Berlin",
    "Asia/Tokyo",
    "Asia/Shanghai",
    "Asia/Singapore",
    "Australia/Sydney",
    "Pacific/Auckland",
  ];

  try {
    return commonTimezones
      .filter((tz) => {
        try {
          new Date().toLocaleString("en-US", { timeZone: tz });
          return true;
        } catch {
          return false;
        }
      })
      .sort();
  } catch {
    return commonTimezones;
  }
};

const TIMEZONE_OPTIONS = generateTimezoneOptions();

const getDefaultTimezone = (): string => {
  try {
    return (
      Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York"
    );
  } catch {
    return "America/New_York";
  }
};

export const UnifiedOnboardingWizard: React.FC<
  UnifiedOnboardingWizardProps
> = ({ mode, onComplete, onSkip }) => {
  const { user, refreshSession } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [stepsCalculated, setStepsCalculated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [profileSaved, setProfileSaved] = useState(false);

  // Get OAuth avatar if available
  const oAuthAvatar =
    (user as any)?.user_metadata?.avatar_url ||
    (user as any)?.user_metadata?.picture ||
    (user as any)?.user_metadata?.avatarUrl ||
    (user as any)?.avatarUrl ||
    (user as any)?.avatar_url;

  const [formData, setFormData] = useState<ProfileFormData>({
    firstName: (user as any)?.first_name || (user as any)?.firstName || "",
    lastName: (user as any)?.last_name || (user as any)?.lastName || "",
    location: null,
    address: "",
    preferences: [],
    favoriteCuisines: [],
    dietaryRestrictions: [],
    accessibilityNeeds: [],
    zipCode: (user as any)?.zip_code || (user as any)?.zipCode || "",
    timezone: (user as any)?.timezone || getDefaultTimezone(),
    avatarUrl: oAuthAvatar || null,
  });

  // Determine which steps to show based on mode and conditions
  const getActiveSteps = useCallback((): Step[] => {
    let steps = ALL_STEPS;

    // Filter by mode
    if (mode === "profile") {
      steps = steps.filter((s) => s.category === "profile");
      // Add complete step at the end for profile mode
      steps = [
        ...steps,
        {
          id: "complete" as StepType,
          category: "profile" as const,
          required: true,
        },
      ];
    } else if (mode === "tour") {
      steps = steps.filter((s) => s.category === "tour");
    }

    // Filter by conditions (skip steps where data already exists)
    const hasProfileSteps = steps.some(
      (s) =>
        s.category === "profile" &&
        s.id !== "welcome" &&
        s.id !== "complete" &&
        (!s.condition || s.condition(formData, user)),
    );

    return steps.filter((step) => {
      // Always show required steps
      if (step.required) return true;

      // For dietary step, only show if other profile steps are shown
      if (step.id === "dietary") {
        return hasProfileSteps;
      }

      // Check condition
      if (step.condition) {
        return step.condition(formData, user);
      }

      return true;
    });
  }, [mode, formData, user]);

  const [activeSteps, setActiveSteps] = useState<Step[]>([]);

  // Calculate active steps ONCE after profile loads - don't recalculate on formData changes
  // to prevent steps from being removed while the user is interacting with them
  useEffect(() => {
    if (!isLoadingProfile && !stepsCalculated) {
      setActiveSteps(getActiveSteps());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingProfile, stepsCalculated]);

  const currentStep = activeSteps[currentStepIndex];
  const totalSteps = activeSteps.length;
  const isLastStep = currentStepIndex === totalSteps - 1;
  const isFirstStep = currentStepIndex === 0;

  // Update avatar preview when OAuth avatar is detected
  useEffect(() => {
    if (oAuthAvatar && !avatarPreview && !formData.avatarUrl) {
      setFormData((prev) => ({ ...prev, avatarUrl: oAuthAvatar }));
    }
  }, [oAuthAvatar, avatarPreview, formData.avatarUrl]);

  // Fetch existing profile data
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setIsLoadingProfile(true);

        const [profileResponse, preferencesResponse] = await Promise.all([
          userApi.getProfile().catch(() => null),
          userApi.getPreferences().catch(() => null),
        ]);

        const profileData =
          (profileResponse as any)?.data?.data ||
          (profileResponse as any)?.data ||
          profileResponse ||
          {};
        const prefsData =
          (preferencesResponse as any)?.data?.profilePreferences ||
          (preferencesResponse as any)?.profilePreferences ||
          preferencesResponse ||
          {};

        // Load from localStorage as fallback
        let localPreferences = null;
        if (user?.id) {
          const stored = localStorage.getItem(`vitly-preferences-${user.id}`);
          if (stored) {
            try {
              localPreferences = JSON.parse(stored);
            } catch {
              // Ignore parse errors
            }
          }
        }

        const cuisines =
          prefsData.cuisine_preferences ||
          prefsData.cuisinePreferences ||
          localPreferences?.favoriteCuisines ||
          [];
        const dietary =
          prefsData.dietary_restrictions ||
          prefsData.dietaryRestrictions ||
          localPreferences?.dietaryRestrictions ||
          [];
        const accessibility =
          prefsData.accessibility_needs ||
          prefsData.accessibilityNeeds ||
          localPreferences?.accessibilityNeeds ||
          [];

        setFormData((prev) => ({
          ...prev,
          firstName:
            profileData.firstName ||
            profileData.first_name ||
            prev.firstName ||
            "",
          lastName:
            profileData.lastName ||
            profileData.last_name ||
            prev.lastName ||
            "",
          zipCode:
            profileData.zipCode || profileData.zip_code || prev.zipCode || "",
          timezone: profileData.timezone || prev.timezone,
          address: profileData.defaultLocationAddress || prev.address || "",
          location:
            profileData.defaultLocationLat && profileData.defaultLocationLng
              ? {
                  lat: profileData.defaultLocationLat,
                  lng: profileData.defaultLocationLng,
                  address: profileData.defaultLocationAddress || "",
                  placeId: profileData.defaultLocationPlaceId || "",
                }
              : prev.location,
          avatarUrl:
            profileData.avatarUrl || profileData.avatar_url || prev.avatarUrl,
          favoriteCuisines:
            cuisines.length > 0 ? cuisines : prev.favoriteCuisines,
          dietaryRestrictions:
            dietary.length > 0 ? dietary : prev.dietaryRestrictions,
          accessibilityNeeds:
            accessibility.length > 0 ? accessibility : prev.accessibilityNeeds,
        }));
      } catch (err) {
        console.error("Failed to fetch profile:", err);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    fetchProfileData();
  }, [user?.id]);

  // Get user's current location
  const handleGetCurrentLocation = async () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser");
      return;
    }

    setIsGettingLocation(true);
    setError(null);

    try {
      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          });
        },
      );

      const { latitude, longitude } = position.coords;

      try {
        const geocoder = new google.maps.Geocoder();
        const result = await geocoder.geocode({
          location: { lat: latitude, lng: longitude },
        });

        if (result.results[0]) {
          const address = result.results[0].formatted_address;
          const placeId = result.results[0].place_id;
          const zipComponent = result.results[0].address_components?.find(
            (c: google.maps.GeocoderAddressComponent) =>
              c.types.includes("postal_code"),
          );
          const zipCode = zipComponent?.short_name || formData.zipCode;

          setFormData((prev) => ({
            ...prev,
            location: { lat: latitude, lng: longitude, address, placeId },
            address,
            zipCode,
          }));
        }
      } catch (geocodeError) {
        console.error("Geocoding failed:", geocodeError);
        setFormData((prev) => ({
          ...prev,
          location: {
            lat: latitude,
            lng: longitude,
            address: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
            placeId: "",
          },
          address: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
        }));
      }
    } catch (err: any) {
      if (err.code === 1) {
        setError("Location access denied. Please enable location permissions.");
      } else {
        setError("Failed to get your location. Please enter it manually.");
      }
    } finally {
      setIsGettingLocation(false);
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type.toLowerCase())) {
      setError("Please select a JPEG, PNG, or WebP image file");
      return;
    }

    if (file.size > 1 * 1024 * 1024) {
      setError("Image size must be less than 1MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => setAvatarPreview(reader.result as string);
    reader.readAsDataURL(file);
    uploadAvatar(file);
  };

  const uploadAvatar = async (file: File) => {
    setIsUploadingAvatar(true);
    setError(null);
    try {
      const result = await authApi.uploadAvatar(file);
      if (result?.avatarUrl) {
        setFormData((prev) => ({ ...prev, avatarUrl: result.avatarUrl }));
        setAvatarPreview(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to upload avatar");
      setAvatarPreview(null);
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const saveProfile = async () => {
    if (profileSaved) return; // Already saved

    setIsSubmitting(true);
    setError(null);

    try {
      const profileData: UpdateProfileData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        zipCode: formData.zipCode,
        timezone: formData.timezone,
      };

      await userApi.updateProfile(profileData);

      if (formData.location) {
        await userApi.updateLocation({
          latitude: formData.location.lat || 0,
          longitude: formData.location.lng || 0,
          address: formData.location.address,
          zipCode: formData.zipCode,
        });
      }

      if (user?.id) {
        const localPreferencesData = {
          preferences: formData.preferences,
          favoriteCuisines: formData.favoriteCuisines,
          dietaryRestrictions: formData.dietaryRestrictions,
          accessibilityNeeds: formData.accessibilityNeeds,
          completedAt: new Date().toISOString(),
        };
        localStorage.setItem(
          `vitly-preferences-${user.id}`,
          JSON.stringify(localPreferencesData),
        );

        const backendPreferencesData = {
          cuisinePreferences: formData.favoriteCuisines,
          dietaryRestrictions: formData.dietaryRestrictions,
          accessibilityNeeds: formData.accessibilityNeeds,
        };

        try {
          await userApi.updatePreferences(backendPreferencesData);
        } catch {
          // Continue - preferences are stored locally as fallback
        }

        localStorage.setItem(`vitly-profile-setup-${user.id}`, "completed");
      }

      await refreshSession();
      setProfileSaved(true);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Failed to save profile. Please try again.",
      );
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    setError(null);

    // Validate current step
    if (
      currentStep?.id === "name" &&
      (!formData.firstName || !formData.lastName)
    ) {
      setError("Please enter your first and last name");
      return;
    }

    if (
      currentStep?.id === "location" &&
      !formData.location &&
      !formData.zipCode &&
      !formData.address
    ) {
      setError(
        "Please set your location, enter an address, or provide your zip code",
      );
      return;
    }

    if (
      currentStep?.id === "cuisines" &&
      formData.favoriteCuisines.length === 0
    ) {
      setError("Please select at least one cuisine");
      return;
    }

    // Save profile when leaving the last profile step (before tour or at dietary)
    const isLastProfileStep =
      currentStep?.category === "profile" &&
      (activeSteps[currentStepIndex + 1]?.category === "tour" ||
        currentStep?.id === "dietary");

    if (isLastProfileStep && !profileSaved) {
      try {
        await saveProfile();
      } catch {
        return; // Error already set
      }
    }

    // Move to next step or complete
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
      setError(null);
    }
  };

  const handleLocationChange = (location: LocationData | null) => {
    setFormData((prev) => ({
      ...prev,
      location: location,
      address: location?.address || "",
    }));
  };

  const toggleCuisine = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      favoriteCuisines: prev.favoriteCuisines.includes(value)
        ? prev.favoriteCuisines.filter((c) => c !== value)
        : [...prev.favoriteCuisines, value],
    }));
  };

  const toggleDietary = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      dietaryRestrictions: prev.dietaryRestrictions.includes(value)
        ? prev.dietaryRestrictions.filter((d) => d !== value)
        : [...prev.dietaryRestrictions, value],
    }));
  };

  const toggleAccessibility = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      accessibilityNeeds: prev.accessibilityNeeds.includes(value)
        ? prev.accessibilityNeeds.filter((a) => a !== value)
        : [...prev.accessibilityNeeds, value],
    }));
  };

  const handleCreateMeetup = () => {
    onComplete();
    navigate("/meetups/new/wizard");
  };

  const handleExploreDashboard = () => {
    onComplete();
    navigate("/dashboard");
  };

  const displayAvatar = avatarPreview || formData.avatarUrl || oAuthAvatar;

  // Mark steps as calculated after first update
  useEffect(() => {
    if (!isLoadingProfile && activeSteps.length > 0) {
      setStepsCalculated(true);
    }
  }, [isLoadingProfile, activeSteps.length]);

  // Loading state - show while loading profile OR while steps haven't been calculated yet
  if (isLoadingProfile || (!stepsCalculated && activeSteps.length === 0)) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl p-8">
          <LoadingSpinner size="lg" />
          <p className="text-gray-600 dark:text-gray-300 mt-4">
            Loading your profile...
          </p>
        </div>
      </div>
    );
  }

  // No steps to show after calculation is complete - this shouldn't happen with required steps
  if (stepsCalculated && activeSteps.length === 0) {
    onComplete();
    return null;
  }

  const renderStepContent = () => {
    switch (currentStep?.id) {
      case "welcome":
        return (
          <div>
            <div className="text-center mb-6">
              {/* Avatar Upload */}
              <div className="mb-4">
                <div className="relative group w-24 h-24 mx-auto">
                  <div className="w-24 h-24 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center overflow-hidden">
                    {isUploadingAvatar ? (
                      <LoadingSpinner size="md" />
                    ) : displayAvatar ? (
                      <img
                        src={displayAvatar}
                        alt="Profile"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    ) : (
                      <User className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                    )}
                  </div>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploadingAvatar}
                    className="absolute bottom-0 right-0 bg-primary text-white p-1.5 rounded-full shadow-lg hover:bg-primary-hover transition-colors"
                  >
                    <Camera className="w-4 h-4" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {displayAvatar ? "Click to change" : "Add photo (optional)"}
                </p>
              </div>

              <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                Welcome to Vitly!
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Let's get you set up. This will only take a minute!
              </p>
            </div>

            {/* Name Fields */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="first-name"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    First Name
                  </label>
                  <input
                    id="first-name"
                    type="text"
                    value={formData.firstName}
                    onChange={(e) =>
                      setFormData({ ...formData, firstName: e.target.value })
                    }
                    placeholder="First name"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label
                    htmlFor="last-name"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Last Name
                  </label>
                  <input
                    id="last-name"
                    type="text"
                    value={formData.lastName}
                    onChange={(e) =>
                      setFormData({ ...formData, lastName: e.target.value })
                    }
                    placeholder="Last name"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case "location":
        return (
          <div>
            <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">
              Where are you located?
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              We'll use this to suggest nearby places for your meetups.
            </p>
            <div className="space-y-4">
              <button
                type="button"
                onClick={handleGetCurrentLocation}
                disabled={isGettingLocation}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors disabled:opacity-50"
              >
                {isGettingLocation ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Getting your location...</span>
                  </>
                ) : (
                  <>
                    <MapPin className="w-5 h-5" />
                    <span>Use My Current Location</span>
                  </>
                )}
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white dark:bg-gray-800 text-gray-500">
                    Or search
                  </span>
                </div>
              </div>

              <LocationAutocomplete
                inputId="location-input"
                value={formData.address}
                onChange={handleLocationChange}
                placeholder="Enter address, city, or place name"
              />

              {formData.location && (
                <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-200 dark:border-green-800">
                  <p className="text-sm text-green-800 dark:text-green-200">
                    Location set: {formData.location.address}
                  </p>
                </div>
              )}

              <div>
                <label
                  htmlFor="zip"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Zip Code
                </label>
                <input
                  id="zip"
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) =>
                    setFormData({ ...formData, zipCode: e.target.value })
                  }
                  placeholder="Enter your zip code"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label
                  htmlFor="timezone"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Timezone
                </label>
                <select
                  id="timezone"
                  value={formData.timezone}
                  onChange={(e) =>
                    setFormData({ ...formData, timezone: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        );

      case "cuisines":
        return (
          <div>
            <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">
              What are your favorite cuisines?
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              We'll prioritize these when suggesting places.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {CUISINE_OPTIONS.map((cuisine) => {
                const isSelected = formData.favoriteCuisines.includes(
                  cuisine.value,
                );
                return (
                  <button
                    key={cuisine.value}
                    onClick={() => toggleCuisine(cuisine.value)}
                    className={`group relative flex items-center gap-2 px-3 py-2.5 text-sm rounded-lg transition-colors min-h-[44px] ${
                      isSelected
                        ? "bg-blue-600 text-white border border-blue-600"
                        : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:border-blue-400"
                    }`}
                  >
                    <span className="text-lg">{cuisine.icon}</span>
                    <span className="truncate flex-1 font-medium">
                      {cuisine.label}
                    </span>
                    {isSelected && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-400 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );

      case "dietary":
        return (
          <div>
            <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">
              Dietary Restrictions & Accessibility
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Help us find places that meet your needs. (Optional)
            </p>

            <div className="mb-6">
              <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                Dietary Restrictions
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {DIETARY_OPTIONS.map((option) => {
                  const isSelected = formData.dietaryRestrictions.includes(
                    option.id,
                  );
                  return (
                    <button
                      key={option.id}
                      onClick={() => toggleDietary(option.id)}
                      className={`group relative flex items-center gap-2 px-3 py-2.5 text-sm rounded-lg transition-colors min-h-[44px] ${
                        isSelected
                          ? "bg-green-600 text-white border border-green-600"
                          : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:border-green-400"
                      }`}
                    >
                      <span className="text-lg">{option.icon}</span>
                      <span className="truncate flex-1 font-medium">
                        {option.label}
                      </span>
                      {isSelected && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center border border-green-600">
                          <Check className="w-3 h-3 text-green-600" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                Wheelchair Accessibility
              </h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                We'll prioritize places with these features.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {WHEELCHAIR_ACCESSIBILITY_OPTIONS.map((option) => {
                  const isSelected = formData.accessibilityNeeds.includes(
                    option.id,
                  );
                  return (
                    <button
                      key={option.id}
                      onClick={() => toggleAccessibility(option.id)}
                      className={`group relative flex items-center gap-2 px-3 py-2.5 text-sm rounded-lg transition-colors min-h-[44px] ${
                        isSelected
                          ? "bg-purple-600 text-white border border-purple-600"
                          : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:border-purple-400"
                      }`}
                    >
                      <span className="text-lg">{option.icon}</span>
                      <span className="truncate flex-1 font-medium">
                        {option.label}
                      </span>
                      {isSelected && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center border border-purple-600">
                          <Check className="w-3 h-3 text-purple-600" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );

      case "dashboard-tour":
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <LayoutDashboard className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">
              Your Dashboard
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              This is your command center. View all your meetups, track upcoming
              plans, and manage your social calendar in one place.
            </p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                <span className="text-gray-700 dark:text-gray-300">
                  Upcoming Meetups
                </span>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
                <Users className="w-5 h-5 text-purple-600 dark:text-purple-400 mx-auto mb-2" />
                <span className="text-gray-700 dark:text-gray-300">
                  Friend Activity
                </span>
              </div>
            </div>
          </div>
        );

      case "meetup-tour":
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Users className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">
              Create Your First Meetup
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Planning a get-together is easy! Just add your friends' locations,
              and we'll find the perfect spot that's fair for everyone.
            </p>
            <div className="bg-gradient-to-r from-orange-50 to-pink-50 dark:from-orange-900/20 dark:to-pink-900/20 p-4 rounded-lg border border-orange-200 dark:border-orange-800">
              <p className="text-sm text-orange-800 dark:text-orange-200">
                <Sparkles className="w-4 h-4 inline mr-1" />
                <strong>Pro tip:</strong> Start with a simple meetup and invite
                a few friends to get comfortable!
              </p>
            </div>
          </div>
        );

      case "support-tour":
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <MessageSquare className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">
              We're Here to Help
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Have questions or feedback? Use the feedback button anytime to
              share your thoughts. We're always improving Vitly based on your
              input!
            </p>
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
              <p className="text-sm text-green-800 dark:text-green-200">
                Look for the feedback button in the bottom corner of any page.
              </p>
            </div>
          </div>
        );

      case "complete":
        return (
          <div className="text-center">
            <div className="mb-6">
              <span className="text-6xl">🎉</span>
            </div>
            <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">
              You're All Set!
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {mode === "profile"
                ? "Your profile is ready. Start exploring Vitly!"
                : "You're ready to start planning amazing meetups!"}
            </p>

            <div className="space-y-3 max-w-sm mx-auto">
              <Button
                variant="primary"
                fullWidth
                onClick={handleCreateMeetup}
                className="py-4"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Create My First Meetup
              </Button>

              <Button
                variant="outline"
                fullWidth
                onClick={handleExploreDashboard}
              >
                Explore Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 z-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              {mode === "tour"
                ? "Quick Tour"
                : mode === "profile"
                  ? "Profile Setup"
                  : "Getting Started"}
            </h2>
            <button
              onClick={onSkip}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Skip for now
            </button>
          </div>

          {/* Progress Bar */}
          <div className="flex items-center space-x-2">
            {Array.from({ length: totalSteps }).map((_, index) => (
              <div
                key={index}
                className={`flex-1 h-2 rounded-full transition-colors ${
                  index <= currentStepIndex
                    ? "bg-primary"
                    : "bg-gray-200 dark:bg-gray-700"
                }`}
              />
            ))}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            Step {currentStepIndex + 1} of {totalSteps}
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {renderStepContent()}

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}
        </div>

        {/* Footer - Hide on complete step */}
        {currentStep?.id !== "complete" && (
          <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
            <div className="flex justify-between gap-3">
              <Button
                onClick={handlePrevious}
                disabled={isFirstStep}
                variant="outline"
              >
                Previous
              </Button>
              <Button
                onClick={handleNext}
                disabled={isSubmitting}
                variant="primary"
                isLoading={isSubmitting}
                loadingText="Saving..."
              >
                {isLastStep ? "Complete" : "Continue"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
